import { trapFocus } from "entrypoints/theme";
import { reconcileAssemblyDependencies, showAssemblyRemovalNotice } from "helpers/cart-assembly-dependencies";
import { debounce, fetchConfig } from "helpers/utils";
class CartItems extends window.HTMLElement {
  constructor() {
    super();
    this._onPageShow = this.onPageShow.bind(this);
    this._onAssemblyCartUpdated = this.onAssemblyCartUpdated.bind(this);
    this._isReconcilingAssembly = false;
    this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status") || document.getElementById("CartDrawer-LineItemStatus");
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener("change", this.debouncedOnChange);
  }
  connectedCallback() {
    window.removeEventListener("pageshow", this._onPageShow);
    window.addEventListener("pageshow", this._onPageShow);
    if (!this.matches("cart-drawer-items")) {
      window.removeEventListener("cart:updated", this._onAssemblyCartUpdated);
      window.addEventListener("cart:updated", this._onAssemblyCartUpdated);
      this.reconcileAssemblyDependenciesOnLoad();
    }
  }
  disconnectedCallback() {
    window.removeEventListener("pageshow", this._onPageShow);
    window.removeEventListener("cart:updated", this._onAssemblyCartUpdated);
  }
  onChange(event) {
    var _a, _b;
    if (this.matches("cart-drawer-items"))
      return;
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      (_b = (_a = document.activeElement) == null ? void 0 : _a.getAttribute) == null ? void 0 : _b.call(_a, "name")
    );
  }
  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents"
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section"
      },
      {
        id: "cart-subtotal",
        section: "cart-subtotal",
        selector: ".shopify-section"
      },
      {
        id: "cart-totals",
        section: "main-cart",
        selector: "#cart-totals"
      }
    ];
  }
  isCheckoutReturn(event) {
    const ref = document.referrer || "";
    return !!((event == null ? void 0 : event.persisted) || ref.includes("/checkout") || ref.includes("/checkouts") || ref.includes("checkout."));
  }
  getVisibleCartCount() {
    const badge = document.querySelector("[data-cart-count-new]");
    const badgeCount = Number.parseInt((badge == null ? void 0 : badge.textContent) || "", 10);
    if (Number.isFinite(badgeCount))
      return badgeCount;
    return Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + (Number.parseInt(quantityInput.value, 10) || 0),
      0
    );
  }
  getMerchandiseItemCount(cart) {
    if (!Array.isArray(cart == null ? void 0 : cart.items))
      return 0;
    return cart.items.reduce((count, item) => {
      var _a;
      if ((_a = item == null ? void 0 : item.properties) == null ? void 0 : _a._package_protection)
        return count;
      return count + Math.max(0, Number(item.quantity) || 0);
    }, 0);
  }
  wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
  applyCartSections(parsedState) {
    this.classList.toggle("is-empty", parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector("cart-drawer");
    if (cartDrawerWrapper)
      cartDrawerWrapper.classList.toggle(
        "is-empty",
        parsedState.item_count === 0
      );
    this.getSectionsToRender().forEach((section) => {
      var _a;
      const container = document.getElementById(section.id);
      const sectionHtml = (_a = parsedState.sections) == null ? void 0 : _a[section.section];
      if (!container || !sectionHtml)
        return;
      const elementToReplace = container.querySelector(section.selector) || container;
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector
      );
    });
  }
  getSectionRequestPayload() {
    return {
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    };
  }
  async getFreshSections(cart) {
    const sectionNames = this.getSectionsToRender().map((section) => section.section);
    const response = await fetch(`?sections=${sectionNames.join(",")}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });
    if (!response.ok)
      throw new Error(`Cart section refresh failed (${response.status})`);
    return {
      ...cart,
      sections: await response.json()
    };
  }
  async reconcileAssemblyDependenciesOnLoad(cart = null) {
    var _a, _b;
    if (this._isReconcilingAssembly)
      return;
    this._isReconcilingAssembly = true;
    const mainCartItems = document.getElementById("main-cart-items");
    try {
      mainCartItems == null ? void 0 : mainCartItems.classList.add("loading");
      let result = await reconcileAssemblyDependencies(
        cart,
        this.getSectionRequestPayload()
      );
      if (!result.changed)
        return;
      if (window.PackageProtection) {
        const protectionState = await window.PackageProtection.sync(result.cart);
        result = {
          ...result,
          cart: (protectionState == null ? void 0 : protectionState.cart) || result.cart
        };
      }
      if (!((_b = (_a = result.cart) == null ? void 0 : _a.sections) == null ? void 0 : _b[this.getSectionsToRender()[0].section])) {
        result = {
          ...result,
          cart: await this.getFreshSections(result.cart)
        };
      }
      this.applyCartSections(result.cart);
      this.currentItemCount = result.cart.item_count;
      if (result.noticeRequired)
        showAssemblyRemovalNotice(this);
      window.dispatchEvent(new CustomEvent("cart:updated", {
        detail: { cart: result.cart }
      }));
    } catch (error) {
      console.error("Assembly dependency initialization error:", error);
      const errors = document.getElementById("cart-errors");
      if (errors)
        errors.textContent = window.cartStrings.error;
    } finally {
      mainCartItems == null ? void 0 : mainCartItems.classList.remove("loading");
      this._isReconcilingAssembly = false;
    }
  }
  onAssemblyCartUpdated(event) {
    var _a;
    if (this._isReconcilingAssembly)
      return;
    const cart = (_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.cart;
    if (!Array.isArray(cart == null ? void 0 : cart.items))
      return;
    this.reconcileAssemblyDependenciesOnLoad(cart);
  }
  async restoreCartAfterCheckout() {
    const expectedCount = this.getVisibleCartCount();
    const sectionNames = this.getSectionsToRender().map((section) => section.section);
    const delays = [250, 700, 1400];
    for (let index = 0; index < delays.length; index += 1) {
      if (delays[index] > 0)
        await this.wait(delays[index]);
      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok)
        throw new Error(`Cart fetch failed (${cartResponse.status})`);
      const cart = await cartResponse.json();
      const restoredCount = this.getMerchandiseItemCount(cart);
      const isLastAttempt = index === delays.length - 1;
      if (Number.isFinite(expectedCount) && restoredCount < expectedCount && !isLastAttempt) {
        continue;
      }
      const sectionsResponse = await fetch(`?sections=${sectionNames.join(",")}`, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!sectionsResponse.ok)
        throw new Error(`Cart section refresh failed (${sectionsResponse.status})`);
      const sections = await sectionsResponse.json();
      this.applyCartSections({
        item_count: cart.item_count,
        sections
      });
      window.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
      const renderedCount = this.getVisibleCartCount();
      if (Number.isFinite(expectedCount) && renderedCount < expectedCount && !isLastAttempt) {
        continue;
      }
      return;
    }
    throw new Error("Unable to restore cart after checkout return");
  }
  async onPageShow(event) {
    if (!this.isCheckoutReturn(event) || this.matches("cart-drawer-items"))
      return;
    try {
      await this.restoreCartAfterCheckout();
    } catch (error) {
      console.error("Cart restore error:", error);
      window.location.reload();
    }
  }
  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), ...{ body } }).then((response) => {
      if (!response.ok)
        throw new Error(`Cart update failed (${response.status})`);
      return response.text();
    }).then(async (state) => {
      var _a;
      let parsedState = JSON.parse(state);
      const assemblyResult = await reconcileAssemblyDependencies(
        parsedState,
        this.getSectionRequestPayload()
      );
      parsedState = assemblyResult.cart;
      if (assemblyResult.changed && window.PackageProtection) {
        const protectionState = await window.PackageProtection.sync(parsedState);
        parsedState = (protectionState == null ? void 0 : protectionState.cart) || parsedState;
      }
      if (!((_a = parsedState == null ? void 0 : parsedState.sections) == null ? void 0 : _a[this.getSectionsToRender()[0].section]))
        parsedState = await this.getFreshSections(parsedState);
      this.applyCartSections(parsedState);
      this.updateLiveRegions(line, parsedState.item_count);
      const cartDrawerWrapper = document.querySelector("cart-drawer");
      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        cartDrawerWrapper ? trapFocus(
          cartDrawerWrapper,
          lineItem.querySelector(`[name="${name}"]`)
        ) : lineItem.querySelector(`[name="${name}"]`).focus();
      } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
        trapFocus(
          cartDrawerWrapper.querySelector("#CartDrawer"),
          cartDrawerWrapper.querySelector('[tabindex="-1"]')
        );
      } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
        trapFocus(
          cartDrawerWrapper,
          document.querySelector(".cart-item-name")
        );
      }
      if (assemblyResult.noticeRequired)
        showAssemblyRemovalNotice(this);
      window.dispatchEvent(new CustomEvent("cart:updated", {
        detail: { cart: parsedState }
      }));
      this.disableLoading();
    }).catch((error) => {
      console.error("Cart update error:", error);
      this.querySelectorAll(".loading-overlay").forEach(
        (overlay) => overlay.classList.add("hidden")
      );
      const errors = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
      if (errors)
        errors.textContent = window.cartStrings.error;
      this.disableLoading();
    });
  }
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      lineItemError.innerHTML = window.cartStrings.quantityError.replace(
        "[quantity]",
        quantityElement.value
      );
    }
    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute("aria-hidden", true);
    const cartStatus = document.getElementById("cart-live-region-text") || document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);
    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1e3);
  }
  getSectionInnerHTML(html, selector) {
    return new window.DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
  enableLoading(line) {
    const mainCartItems = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("loading");
    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );
    [...cartItemElements, ...cartDrawerItemElements].forEach(
      (overlay) => overlay.classList.remove("hidden")
    );
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }
  disableLoading() {
    const mainCartItems = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("loading");
  }
}
window.customElements.define("cart-items", CartItems);
export {
  CartItems as default
};
